import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getCourseAssessment, submitAssessmentWithResult } from '../../../../services/userService';

function CourseQuiz({ assessmentId, moduleData, moduleName, lessonName, onAssessmentComplete, currentLessonIndex, totalLessons, onNextLesson }) {
  const navigate = useNavigate();
  // console.log('moduleData', moduleData);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [quizData, setQuizData] = useState({
    duration: 5,
    pass_percentage: 60,
    total_questions: 0,
    is_attempted: 0,
    result: null
  });
  const [submissionResult, setSubmissionResult] = useState(null);

  const completionNotifiedRef = useRef(false);

  useEffect(() => {
    getAssessmentQuestions();
    // Reset component state when assessment changes
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setQuizStarted(false);
    setSubmissionResult(null);
    completionNotifiedRef.current = false;
  }, [assessmentId]);

  useEffect(() => {
    // Only mark complete from server-provided completion, not just from "attempted",
    // otherwise progress can jump just by starting/revisiting the quiz.
    // Also, notify parent only once; repeated notifications can cause module list churn
    // and prevent the sidebar from staying expanded when the user clicks other modules.
    if (!completionNotifiedRef.current && moduleData?.completed === 1 && onAssessmentComplete) {
      completionNotifiedRef.current = true;
      onAssessmentComplete(moduleData.id, true);
    }
  }, [moduleData?.completed, moduleData?.id, onAssessmentComplete]);

  const getAssessmentQuestions = async () => {
    try {
      const response = await getCourseAssessment({
        assessment_id: assessmentId || moduleData.id
      });

      if (response.success) {
        const payload = response.data && typeof response.data === 'object' ? response.data : response;
        const questionsList = payload.questions || payload.data || payload || [];
        setQuestions(Array.isArray(questionsList) ? questionsList : []);

        const quizInfo = {
          duration: payload.duration || response.duration || 5,
          pass_percentage: payload.pass_percentage || response.pass_percentage || 60,
          total_questions: payload.total_questions || response.total_questions || 0,
          is_attempted: payload.is_attempted || response.is_attempted || 0,
          result: payload.result || response.result || null
        };
        setQuizData(quizInfo);
        setTimeLeft((quizInfo.duration || 5) * 60 || 300);
      }
      // console.log('assessment questions', response);
    } catch (error) {
      // console.log('error-----------------:', error);
    }
  };

  // Timer logic
  React.useEffect(() => {
    if (quizStarted && timeLeft > 0 && !showResults) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowResults(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quizStarted, timeLeft, showResults]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleStartQuiz = () => {
    // When retaking, clear any previous submission state so UI doesn't accumulate
    // (and refresh server data so attempt/result reflects latest attempt)
    getAssessmentQuestions();
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setSubmissionResult(null);
    setTimeLeft(quizData.duration * 60 || 300);
    setQuizStarted(true);
  };

  const handleOptionSelect = (questionId, optionId) => {
    setSelectedAnswers(prev => {
      const currentSelections = prev[questionId] || [];
      const updatedSelections = currentSelections.includes(optionId)
        ? currentSelections.filter(id => id !== optionId)
        : [...currentSelections, optionId];

      return {
        ...prev,
        [questionId]: updatedSelections
      };
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const answers = questions.map(question => {
        const selectedOptionIds = selectedAnswers[question.id] || [];
        const correctOptions = question.options.filter(opt => opt.is_correct);

        return {
          question_id: question.id,
          selected_option_id: selectedOptionIds,
          correct_option_id: correctOptions.map(opt => opt.id),
          correct_value: correctOptions.map(opt => opt.content_value)
        };
      });

      const payload = {
        assessment_id: moduleData.id,
        answers: answers
      };

      // console.log('Submitting assessment with payload:', payload);

      const response = await submitAssessmentWithResult(payload);
      // console.log('Assessment submission response:', response);

      setSubmissionResult(response.result);
      setShowResults(true);

      // Call the completion callback when assessment is submitted
      if (onAssessmentComplete && response.result) {
        completionNotifiedRef.current = true;
        onAssessmentComplete(moduleData.id, true);
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  const calculateScore = () => {
    let score = 0;
    questions.forEach(question => {
      const selectedOptionIds = selectedAnswers[question.id] || [];
      const correctOptions = question.options.filter(opt => opt.is_correct);
      if (selectedOptionIds.every(id => correctOptions.some(opt => opt.id === id))) {
        score++;
      }
    });
    return score;
  };

  const getScorePercentage = () => {
    return (calculateScore() / questions.length) * 100;
  };

  const renderQuestionContent = (question) => {
    switch (question.question_content_type) {
      case 'image':
        return (
          <div>
            <p className="text-base font-medium text-font-primary mb-3">{question.question}</p>
            {question.question_media_url && (
              <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
                <img src={question.question_media_url} alt={question.question} className="w-full max-h-64 object-contain bg-gray-50" />
              </div>
            )}
          </div>
        );
      case 'audio':
        return (
          <div>
            <p className="text-base font-medium text-font-primary mb-3">{question.question}</p>
            {question.question_media_url && (
              <div className="mt-3">
                <audio controls className="w-full max-w-md">
                  <source src={question.question_media_url} type="audio/mpeg" />
                </audio>
              </div>
            )}
          </div>
        );
      case 'text':
      default:
        return <p className="text-base font-medium text-font-primary">{question.question}</p>;
    }
  };

  const getOptionLetter = (index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
  };

  const renderOptionContent = (option) => {
    switch (option.content_type) {
      case 'image':
        return (
          <div className="flex flex-col gap-2">
            {option.content_value && <span>{option.content_value}</span>}
            {option.media_url && (
              <img src={option.media_url} alt={option.content_value} className="max-h-32 rounded-lg object-contain bg-gray-50" />
            )}
          </div>
        );
      case 'audio':
        return (
          <div className="flex flex-col gap-2">
            {option.content_value && <span>{option.content_value}</span>}
            {option.media_url && (
              <audio controls className="w-full max-w-xs">
                <source src={option.media_url} type="audio/mpeg" />
              </audio>
            )}
          </div>
        );
      case 'text':
      default:
        return <span>{option.content_value}</span>;
    }
  };

  const renderResults = () => {
    if (!showResults || !submissionResult) return null;
    const passed = submissionResult.pass_status === 'pass';

    return (
      <div className="p-6 sm:p-8 max-w-xl mx-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-font-primary">Assessment Results</h2>
          {(moduleName || lessonName) && (
            <p className="text-sm text-gray-500 mt-1">
              {moduleName ? `Module: ${moduleName}` : ''}{moduleName && lessonName ? ' · ' : ''}{lessonName ? `Lesson: ${lessonName}` : ''}
            </p>
          )}
        </div>

        <div className="flex flex-col items-center mb-8">
          <div className="w-28 h-28 rounded-full flex flex-col items-center justify-center border-4 border-primary/20 bg-primary/5">
            <span className="text-2xl font-bold text-primary">{submissionResult.percentage}.0%</span>
            <span className="text-xs font-medium text-font-secondary">Score</span>
          </div>
          <span
            className={`inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl text-sm font-medium ${
              passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            <Icon icon={passed ? 'mdi:check-circle' : 'mdi:close-circle'} width="20" height="20" />
            {passed ? 'Passed' : 'Failed'}
          </span>
        </div>
        <ul className="space-y-3 mb-8">
          {[
            { icon: 'mdi:help-circle-outline', label: 'Total Questions', value: submissionResult.total_questions },
            { icon: 'mdi:checkbox-marked-circle-outline', label: 'Attempted', value: submissionResult.attempted_questions },
            { icon: 'mdi:trophy-outline', label: 'Passing Score', value: `${quizData.pass_percentage}%` },
            { icon: 'mdi:check-circle', label: 'Correct', value: submissionResult.correct_answers, highlight: true },
            { icon: 'mdi:close-circle', label: 'Wrong', value: submissionResult.wrong_answers, wrong: true },
          ].map((item) => (
            <li
              key={item.label}
              className={`flex items-center gap-3 py-2.5 px-4 rounded-xl text-sm ${
                item.wrong ? 'bg-red-50 text-red-800' : item.highlight ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-font-secondary'
              }`}
            >
              <Icon icon={item.icon} width="20" height="20" className="shrink-0" />
              <span className="flex-1">{item.label}</span>
              <span className="font-semibold tabular-nums">{item.value}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium bg-amber-500 hover:bg-amber-600 text-white transition"
            onClick={handleStartQuiz}
          >
            <Icon icon="mdi:refresh" width="20" height="20" />
            Start Again
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium bg-primary hover:bg-primary/90 text-white transition"
            onClick={handleReviewAssessment}
          >
            <Icon icon="mdi:eye" width="20" height="20" />
            Review Assessment Result
          </button>
          {typeof onNextLesson === 'function' && totalLessons > 0 && (
            <button
              type="button"
              onClick={onNextLesson}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] transition"
            >
              {currentLessonIndex >= totalLessons ? 'Explore Courses' : 'Next Lesson'}
              <Icon icon="mdi:arrow-right" width="20" height="20" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleReviewAssessment = () => {
    // console.log('moduleData', moduleData.id);
    navigate(`/user/courses/Result`, {
      state: {
        assessmentId: moduleData.id,
        moduleName,
        lessonName,
      }
    });
  };

  if (showResults && submissionResult) {
    return renderResults();
  }

  if (!quizStarted) {
    return (
      <div className="p-6 sm:p-8 max-w-2xl mx-auto">
        <h1 className="text-2xl font-semibold text-font-primary mb-6">Assessment</h1>

        {quizData.is_attempted === 1 && quizData.result && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Previous Attempt</h3>
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold ${
                  quizData.result.pass_status === 'pass' ? 'bg-green-100 text-green-800 border-2 border-green-300' : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}
              >
                <Icon icon={quizData.result.pass_status === 'pass' ? 'mdi:check-circle' : 'mdi:close-circle'} width="20" height="20" />
                {quizData.result.pass_status === 'pass' ? 'Passed' : 'Failed'}
              </span>
            </div>
            <div className="flex items-center gap-6 mb-5">
              <div className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center bg-white border-2 border-blue-300 shadow-sm">
                <span className="text-2xl font-bold text-blue-600">{quizData.result.percentage}%</span>
                <span className="text-xs text-gray-600 font-medium">Score</span>
              </div>
              <div className="grid grid-cols-2 gap-3 flex-1">
                <div className="bg-white rounded-xl p-3 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-600 text-xs mb-1">
                    <Icon icon="mdi:help-circle-outline" width="16" height="16" />
                    Total
                  </div>
                  <p className="text-xl font-bold text-gray-900">{quizData.result.total_questions}</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-600 text-xs mb-1">
                    <Icon icon="mdi:checkbox-marked-circle-outline" width="16" height="16" />
                    Attempted
                  </div>
                  <p className="text-xl font-bold text-gray-900">{quizData.result.attempted_questions}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 text-xs mb-1">
                    <Icon icon="mdi:check-circle" width="16" height="16" />
                    Correct
                  </div>
                  <p className="text-xl font-bold text-green-700">{quizData.result.correct_answers}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                  <div className="flex items-center gap-2 text-red-700 text-xs mb-1">
                    <Icon icon="mdi:close-circle" width="16" height="16" />
                    Wrong
                  </div>
                  <p className="text-xl font-bold text-red-700">{quizData.result.wrong_answers}</p>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline"
              onClick={handleReviewAssessment}
            >
              <Icon icon="mdi:eye" width="18" height="18" />
              Review Assessment Result
            </button>
          </div>
        )}

        {quizData.is_attempted === 1 && !quizData.result && (
          <div className="mb-8 p-5 rounded-2xl bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">Previous Attempt</p>
                <p className="text-xs text-gray-500">You have already attempted this assessment.</p>
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline"
                onClick={handleReviewAssessment}
              >
                <Icon icon="mdi:eye" width="18" height="18" />
                Review Assessment Result
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-6 p-4 rounded-xl bg-primary-subtle/50 border border-primary/10">
          <span className="inline-flex items-center gap-2 text-sm text-font-secondary">
            <Icon icon="mdi:help-circle-outline" width="20" height="20" className="text-primary" />
            {quizData.total_questions} questions
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-font-secondary">
            <Icon icon="mdi:clock-outline" width="20" height="20" className="text-primary" />
            {quizData.duration} min
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-font-secondary">
            <Icon icon="mdi:trophy-outline" width="20" height="20" className="text-primary" />
            Pass: {quizData.pass_percentage}%
          </span>
        </div>

        <div className="mb-8 p-5 rounded-xl border border-gray-200 bg-white">
          <h2 className="text-base font-semibold text-font-primary mb-3">Instructions</h2>
          <ul className="space-y-2 text-sm text-font-secondary list-disc list-inside">
            <li>Read each question carefully before answering</li>
            <li>You can go back to previous questions</li>
            <li>Timer starts when you begin</li>
            <li>Questions may contain text, images, or audio</li>
          </ul>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white transition ${
              quizData.is_attempted ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary hover:bg-primary/90'
            }`}
            onClick={handleStartQuiz}
          >
            <Icon icon="mdi:play" width="20" height="20" />
            {quizData.is_attempted ? 'Start Again' : 'Start Quiz'}
          </button>
          {moduleData?.completed === 1 && typeof onNextLesson === 'function' && totalLessons > 0 && (
            <button
              type="button"
              onClick={onNextLesson}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] transition"
            >
              {currentLessonIndex >= totalLessons ? 'Explore Courses' : 'Next Lesson'}
              <Icon icon="mdi:arrow-right" width="20" height="20" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  if (!question) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6 sm:p-8 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 py-3 px-4 rounded-xl bg-gray-50 border border-gray-100">
        <span className="text-sm font-medium text-font-secondary">
          Question <span className="font-semibold text-font-primary">{currentQuestion + 1}</span>
          <span className="text-font-secondary">/{questions.length}</span>
        </span>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-font-primary tabular-nums">
          <Icon icon="mdi:clock-outline" width="18" height="18" />
          {formatTime(timeLeft)}
        </span>
      </div>

      <div className="mb-6 p-5 rounded-xl border border-gray-200 bg-white">
        {renderQuestionContent(question)}
      </div>

      <p className="text-sm font-medium text-font-secondary mb-3">Select your answer(s):</p>
      <div className="space-y-2 mb-8">
        {question.options.map((option, index) => {
          const isSelected = (selectedAnswers[question.id] || []).includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleOptionSelect(question.id, option.id)}
              className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-primary bg-primary-subtle text-primary'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-font-primary'
              }`}
            >
              <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-gray-100 font-semibold text-sm text-font-secondary">
                {getOptionLetter(index)}
              </span>
              <span className="min-w-0 flex-1 text-sm">{renderOptionContent(option)}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
        <div>
          {currentQuestion > 0 && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-font-secondary hover:bg-gray-100 transition"
              onClick={handlePrevious}
            >
              <Icon icon="mdi:chevron-left" width="20" height="20" />
              Previous
            </button>
          )}
        </div>
        <div>
          {!isLastQuestion ? (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={handleNext}
              disabled={!(selectedAnswers[question.id] || []).length}
            >
              Next
              <Icon icon="mdi:chevron-right" width="20" height="20" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={handleSubmit}
              disabled={!(selectedAnswers[question.id] || []).length}
            >
              Submit
              <Icon icon="mdi:check" width="20" height="20" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CourseQuiz;